"use client"

import * as React from "react"
import { cn } from '../../lib/utils'
import { UIBox } from './ui-box'
import { UITypography } from '../display/ui-typography'
import { UISeparator } from './ui-separator'

export interface UIFooterBusinessInfo {
  businessNumber?: string
  representative?: string
  address?: string
  customerService?: string
}

export interface UIFooterBusinessInfoLabels {
  businessNumber?: string
  representative?: string
  customerService?: string
}

export interface UIFooterLink {
  label: string
  href?: string
  onClick?: () => void
}

export interface UIFooterProps extends React.HTMLAttributes<HTMLElement> {
  /** Copyright text */
  copyright?: string
  /** Business information */
  businessInfo?: UIFooterBusinessInfo
  /** Labels for business info fields (for i18n) */
  businessInfoLabels?: UIFooterBusinessInfoLabels
  /** Show business info section */
  showBusinessInfo?: boolean
  /** Footer links (e.g., terms, privacy) */
  links?: UIFooterLink[]
  /** Custom link renderer */
  renderLink?: (link: UIFooterLink, index: number) => React.ReactNode
}

const DEFAULT_BUSINESS_INFO: UIFooterBusinessInfo = {
  businessNumber: '748-86-03267',
  representative: '정무경',
  address: '서울특별시 서초구 강남대로51길 1, 511 TOWER 7층 (서초동)',
  customerService: '02-588-1002',
}

const DEFAULT_BUSINESS_INFO_LABELS: UIFooterBusinessInfoLabels = {
  businessNumber: '사업자등록번호',
  representative: '대표',
  customerService: '고객센터',
}

export const UIFooter = React.forwardRef<HTMLElement, UIFooterProps>(
  (
    {
      className,
      copyright = '© 2023 Seahorse AI. All rights reserved.',
      businessInfo,
      businessInfoLabels,
      showBusinessInfo = true,
      links = [],
      renderLink,
      children,
      ...props
    },
    ref
  ) => {
    const info = businessInfo || DEFAULT_BUSINESS_INFO
    const labels = businessInfoLabels || DEFAULT_BUSINESS_INFO_LABELS

    const defaultRenderLink = (link: UIFooterLink, index: number) => (
      <button
        key={index}
        type="button"
        onClick={link.onClick}
        className="text-xs h-auto py-1 px-2 hover:bg-accent hover:text-accent-foreground rounded-md transition-colors"
      >
        {link.label}
      </button>
    )

    return (
      <footer
        ref={ref}
        className={cn("py-6 mt-auto border-t bg-background", className)}
        {...props}
      >
        <UIBox className="flex flex-col gap-2 items-center justify-center w-full">
          <UITypography variant="muted" className="text-xs text-center">
            {copyright}
          </UITypography>

          {showBusinessInfo && info && (
            <>
              <UITypography variant="muted" className="text-xs text-center">
                {labels.businessNumber} {info.businessNumber} | {labels.representative} : {info.representative}
              </UITypography>
              <UITypography variant="muted" className="text-xs text-center">
                {info.address} | {labels.customerService} : {info.customerService}
              </UITypography>
            </>
          )}

          {links.length > 0 && (
            <UIBox className="flex items-center justify-center gap-2 mt-1">
              {links.map((link, index) => (
                <React.Fragment key={index}>
                  {index > 0 && (
                    <UISeparator orientation="vertical" className="h-3" />
                  )}
                  {renderLink ? renderLink(link, index) : defaultRenderLink(link, index)}
                </React.Fragment>
              ))}
            </UIBox>
          )}

          {children}
        </UIBox>
      </footer>
    )
  }
)

UIFooter.displayName = "UIFooter"
